import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import GoogleMap from './GoogleMap';
import '../../assets/css/PropertyDetail.css';

const PropertyDetail = () => {
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Add new state for lightbox
    const [photoIndex, setPhotoIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchProperty = async () => {
            try {
                const { propertyId, root } = window.wpApiSettings;
                console.log('Fetching property:', propertyId);

                const response = await fetch(`${root}wp/v2/property/${propertyId}?_embed&acf_format=standard`);
                if (!response.ok) throw new Error('Failed to fetch property data');

                const data = await response.json();
                console.log('Received property data:', data);
                if (isMounted) {
                    setProperty(data);
                    setLoading(false);
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error:', error);
                    setError(error.message);
                    setLoading(false);
                }
            }
        };

        fetchProperty();

        return () => {
            isMounted = false;
        };
    }, []);

    if (loading) return <div className="loading">Loading property details...</div>;
    if (error) return <div className="error">Error: {error}</div>;
    if (!property) return <div>Property not found</div>;

    // Format price with commas
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    };

    // Format address parts safely
    const formatAddress = (address) => {
        if (typeof address === 'string') return address;

        const parts = [];
        if (address?.street) parts.push(address.street);
        if (address?.city) parts.push(address.city);
        if (address?.state) parts.push(address.state);
        if (address?.zip_code) parts.push(address.zip_code);

        return parts.join(', ') || 'Address not available';
    };

    return (
        <div id="property-detail-root" data-property-id={property.id}>
            <div className="property-detail-page">
                <div className="property-header">
                    <h1>{property.title?.rendered}</h1>
                    <div className="property-price">
                        {console.log('Full ACF object:', property.acf)}
                        {property.acf?.price ? 
                            formatPrice(property.acf.price) : 
                            (property.acf?.pricing_alternative || 'Price not available')}
                    </div>
                </div>

                {/* Featured Image */}
                <div className="property-featured-image">
                    {property.fimg_url ? (
                        <img
                            src={property.fimg_url}
                            alt={property.title.rendered}
                        />
                    ) : property._embedded?.['wp:featuredmedia']?.[0]?.source_url ? (
                        <img
                            src={property._embedded['wp:featuredmedia'][0].source_url}
                            alt={property.title.rendered}
                        />
                    ) : (
                        <div className="property-image-placeholder">
                            No Image Available
                        </div>
                    )}
                </div>

                {/* Quick Info */}
                <div className="property-quick-info">
                    <div className="info-item">
                        <span className="label">Property Type</span>
                        <span className="value">{property.acf?.property_type || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Bedrooms</span>
                        <span className="value">{property.acf?.bedrooms || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Bathrooms</span>
                        <span className="value">{property.acf?.bathrooms || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Square Footage</span>
                        <span className="value">
                            {property.acf?.square_footage ?
                                `${Number(property.acf.square_footage).toLocaleString()} sq ft` :
                                'N/A'
                            }
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Lot Size</span>
                        <span className="value">
                            {property.acf?.lot_size ?
                                `${Number(property.acf.lot_size).toLocaleString()} acres` :
                                'N/A'
                            }
                        </span>
                    </div>
                    <div className="info-item">
                        <span className="label">Year Built</span>
                        <span className="value">{property.acf?.year_built || 'N/A'}</span>
                    </div>
                </div>

                {/* Property Description */}
                <div className="property-description">
                    <h2>Description</h2>
                    <div dangerouslySetInnerHTML={{
                        __html: property.acf?.property_description || property.content?.rendered || 'No description available'
                    }} />
                </div>

                {/* Address and Map Section */}
                <div className="property-location">
                    <h2>Location</h2>
                    <p className="text-center">{formatAddress(property.acf?.address)}</p>
                    {property.acf?.address && (
                        <div className="property-map">
                            <GoogleMap address={formatAddress(property.acf.address)} />
                        </div>
                    )}

                    <div className="location-details-container">
                        {/* Neighborhood Information */}
                        {property.acf?.neighborhood_information && (
                            <div className="neighborhood-information">
                                <h3>About the Neighborhood</h3>
                                <div dangerouslySetInnerHTML={{
                                    __html: property.acf.neighborhood_information
                                }} />
                            </div>
                        )}

                        {/* Nearby Amenities */}
                        {property.acf?.nearby_amenities && property.acf.nearby_amenities.length > 0 && (
                            <div className="nearby-amenities">
                                <h3>Nearby Amenities</h3>
                                <ul className="d-flex justify-content-around">
                                    {property.acf.nearby_amenities.map((item, index) => (
                                        <li key={index}>{item.amenity}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* Amenities Section */}
                {property.acf?.amenities && (
                    <div className="property-amenities">
                        <h2>Property Features</h2>
                        <ul className="amenities-list">
                            {property.acf.amenities.map((feature, index) => (
                                <li key={index} className="amenity-item">
                                    {feature.feature_icon && (
                                        <span className={`dashicons ${feature.feature_icon}`}></span>
                                    )}
                                    <h5>{feature.feature_name}</h5>
                                    {feature.feature_description && (
                                        <p>{feature.feature_description}</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Property Media */}
                {(property.acf?.virtual_tour_link || property.acf?.floor_plans) && (
                    <div className="property-media">
                        <h2>Property Media</h2>
                        {/* Photo Gallery */}
                        {property.acf?.gallery && (
                            <div className="property-gallery">
                                <div className="gallery-grid">
                                    {property.acf.gallery.map((imageUrl, index) => (
                                        <div
                                            key={index}
                                            className="gallery-item"
                                            onClick={() => {
                                                setPhotoIndex(index);
                                                setIsOpen(true);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={`Property photo ${index + 1}`}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Lightbox component */}
                                <Lightbox
                                    open={isOpen}
                                    close={() => setIsOpen(false)}
                                    index={photoIndex}
                                    slides={property.acf.gallery.map(url => ({ src: url }))}
                                />
                            </div>
                        )}

                        {/* Virtual Tour */}
                        {property.acf?.virtual_tour_link && (
                            <div className="virtual-tour">
                                <h3>Virtual Tour</h3>
                                <a
                                    href={property.acf.virtual_tour_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="virtual-tour-link"
                                >
                                    View Virtual Tour
                                </a>
                            </div>
                        )}

                        {/* Floor Plans */}
                        {property.acf?.floor_plans && (
                            <div className="floor-plans">
                                <h3>Floor Plans</h3>
                                <div className="floor-plans-grid">
                                    {property.acf.floor_plans.map((plan, index) => (
                                        <div key={index} className="floor-plan-item">
                                            <h4>{plan.floor_plan_name}</h4>
                                            <img
                                                src={plan.floor_plan_image?.url || plan.floor_plan_image}
                                                alt={plan.floor_plan_name}
                                                onClick={() => {
                                                    setPhotoIndex(index);
                                                    setIsOpen(true);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            {plan.floor_plan_details && (
                                                <div
                                                    className="floor-plan-details"
                                                    dangerouslySetInnerHTML={{ __html: plan.floor_plan_details }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Update Lightbox slides to use the correct image path */}
                                <Lightbox
                                    open={isOpen}
                                    close={() => setIsOpen(false)}
                                    index={photoIndex}
                                    slides={property.acf.floor_plans.map(plan => ({
                                        src: plan.floor_plan_image?.url || plan.floor_plan_image
                                    }))}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Available Date */}
                {property.acf?.available_date && (
                    <div className="property-availability">
                        <h2>Availability</h2>
                        <p>Available from: {property.acf.available_date}</p>
                    </div>
                )}
            </div>

            {/* Move contact section here, outside property-detail-page but inside property-detail-root */}
            {property.acf?.contact_name && (
                <div className="property-contact">
                    <h2 className="text-center">Property Contact Information</h2>
                    <div className="contact-container pt-5 d-flex justify-content-evenly align-items-center flex-wrap">
                        {property.acf.contact_image && (
                            <div className="contact-image">
                                <img
                                    src={property.acf.contact_image?.url || property.acf.contact_image}
                                    alt={property.acf.contact_name}
                                />
                            </div>
                        )}
                        <div className="contact-details mt-4">
                            <h3>{property.acf.contact_name}</h3>
                            {property.acf.contact_company_name && (
                                <div>
                                    <span>Company Name: </span>
                                    <p className="company-name bold">{property.acf.contact_company_name}</p>
                                </div>
                            )}

                            {property.acf.contact_address && (
                                <div className="contact-address">
                                    <p>{property.acf.contact_address.contact_street_address}</p>
                                    <p>
                                        {property.acf.contact_address.contact_city}, {' '}
                                        {property.acf.contact_address.contact_state} {' '}
                                        {property.acf.contact_address.contact_zip_code}
                                    </p>
                                </div>
                            )}

                            {property.acf.contact_office_phone_number && (
                                <p className="contact-office-phone">
                                    <span className="contact-label">Office Phone: </span>
                                    <a href={`tel:${property.acf.contact_office_phone_number}`}>
                                        {property.acf.contact_office_phone_number}
                                    </a>
                                </p>
                            )}
                            {property.acf.contact_mobile_number && (
                                <p className="contact-mobile-phone">
                                    <span className="contact-label">Mobile Phone: </span>
                                    <a href={`tel:${property.acf.contact_mobile_number}`}>
                                        {property.acf.contact_mobile_number}
                                    </a>
                                </p>
                            )}

                            {property.acf.contact_fax_number && (
                                <p className="contact-fax">
                                    <span className="contact-label">Fax: </span>
                                    <a href={`tel:${property.acf.contact_fax_number}`}>
                                        {property.acf.contact_fax_number}
                                    </a>
                                </p>
                            )}

                            {property.acf.contact_email && (
                                <p className="contact-email">
                                    <span className="contact-label">Email: </span>
                                    <a href={`mailto:${property.acf.contact_email}`}>
                                        {property.acf.contact_email}
                                    </a>
                                </p>
                            )}

                            {property.acf.contact_website && (
                                <p className="contact-website">
                                    <span className="contact-label">Website: </span>
                                    <a
                                        href={property.acf.contact_website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Visit Website
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyDetail;